import { string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import algoliasearch from 'algoliasearch/lite';
import CategoryIcon from '../../../../components/CategoryIcon/CategoryIcon';
import {
  categoryDisplayName,
  getCategoryDisplayId,
  transformDbCategories,
} from '../../../../util/data';
import { categoriesListing } from '../../LandingPage.duck';
import { createResourceLocatorString } from '../../../../util/routes';
import { buildPublishedFiltersString } from '../../../AlgoliaSearchPage/AlgoliaSearchPage.filters';
import styles from './TopCategories.module.css';

const ALGOLIA_APP_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_API_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;
const ALGOLIA_DEALS_INDEX = process.env.REACT_APP_ALGOLIA_DEALS_INDEX;
// Deals store the linked listing's subcategory id (legacy or canonical) in listingsCategories.
const DEAL_CATEGORY_FACET = 'publicData.listingsCategories';

const searchClient =
  ALGOLIA_APP_ID && ALGOLIA_API_KEY
    ? algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY, { _useRequestCache: true })
    : null;

/**
 * TopCategories — horizontal-scroll strip of vertical category cards.
 * Centered squircle image, title, and "{count}+ deals" (Lovable home style).
 */
const TopCategories = props => {
  const {
    intl,
    history,
    routeConfiguration,
    panelMode = false,
  } = props;

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const [dealSubCounts, setDealSubCounts] = useState({});

  useEffect(() => {
    const fetchCategoriesData = () => {
      try {
        dispatch(categoriesListing()).then(data => {
          setCategories(data);
        });
      } catch (error) {
        console.error(
          intl.formatMessage({ id: 'AddDealForm.failedToFetchCategories' }),
          error
        );
      }
    };

    fetchCategoriesData();
  }, [dispatch]);

  // Facet-only search on the deals index for a real "{count}+ deals" subtitle per category card.
  useEffect(() => {
    if (!searchClient || !ALGOLIA_DEALS_INDEX) return undefined;

    let cancelled = false;

    const fetchDealCounts = async () => {
      try {
        const response = await searchClient.search([
          {
            indexName: ALGOLIA_DEALS_INDEX,
            params: {
              query: '',
              hitsPerPage: 0,
              facets: [DEAL_CATEGORY_FACET],
              maxValuesPerFacet: 500,
              // Published deals only, matching the deals search page.
              filters: buildPublishedFiltersString([]),
            },
          },
        ]);

        if (cancelled) return;
        setDealSubCounts(response?.results?.[0]?.facets?.[DEAL_CATEGORY_FACET] || {});
      } catch (error) {
        console.error('Failed to fetch deal counts:', error);
      }
    };

    fetchDealCounts();

    return () => {
      cancelled = true;
    };
  }, []);

  const organizedCategories = useMemo(() => transformDbCategories(categories), [categories]);

  // A category's public identity is its slug: that is what listings write into
  // publicData, what Algolia indexes as a facet value, and what the search page
  // reads back out of the URL. The row uuid never leaves our own memory.
  const idOf = category => String(category?.slug || category?.id);

  // Deals are tagged with the *subcategory* of their linked service listing, so
  // a parent card's count is the sum over its children's facet counts.
  //
  // This previously resolved each facet key through getCategoryDisplayId and
  // then matched it with Number(c.id) — a leftover from the legacy numeric
  // taxonomy. Against uuid-keyed DB rows Number(c.id) is NaN, so the lookup
  // could never hit and every card fell back to the countless badge.
  const parentDealCounts = useMemo(() => {
    const counts = {};
    organizedCategories.forEach(parent => {
      const total = (parent.childCategory || []).reduce(
        (sum, child) => sum + Number(dealSubCounts[idOf(child)] || 0),
        0
      );
      if (total > 0) counts[idOf(parent)] = total;
    });
    return counts;
  }, [dealSubCounts, organizedCategories]);

  const goToCategory = parentCategoryId => {
    history.push(
      createResourceLocatorString(
        'AlgoliaSearchPage',
        routeConfiguration,
        { listingType: 'deals' },
        { 'publicData.listingsCategories': parentCategoryId }
      )
    );
  };

  // Count of published deals under the parent (sum of its subcategories' deal counts).
  const renderBadge = parentCategory => {
    const count = Number(parentDealCounts[idOf(parentCategory)] || 0);
    if (count > 0) {
      return intl.formatMessage(
        { id: 'TopCategories.cardBadgeCount' },
        { count }
      );
    }
    return intl.formatMessage({ id: 'TopCategories.cardBadge' });
  };

  const hasCategories =
    Array.isArray(organizedCategories) && organizedCategories.length > 0;

  return (
    <section
      className={classNames(
        styles.topCategoriesWrapper,
        panelMode && styles.panelMode,
        arabicLanguage && styles.arabicText
      )}
    >
      <div className={styles.categoriesCard}>
        {hasCategories ? (
          organizedCategories.map(parentCategory => (
            <div
              key={parentCategory.id}
              className={styles.categoriesList}
              onClick={() => goToCategory(idOf(parentCategory))}
              role="link"
              tabIndex={0}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  goToCategory(idOf(parentCategory));
                }
              }}
            >
              <CategoryIcon category={parentCategory} className={styles.categoriesImage} />
              <div className={styles.categoriesTitle}>
                {categoryDisplayName({
                  intl,
                  ref: parentCategory.slug || parentCategory.id,
                  resolved: parentCategory,
                  legacyDisplayId: getCategoryDisplayId(parentCategory.id, categories),
                })}
              </div>
              <span className={styles.categoryBadge}>
                {renderBadge(parentCategory)}
              </span>
            </div>
          ))
        ) : (
          <div className={styles.noCategories}>
            {intl.formatMessage({ id: 'TopCategories.noCategoriesAvailable' })}
          </div>
        )}
      </div>
    </section>
  );
};

TopCategories.defaultProps = {
  className: null,
  rootClassName: null,
  panelMode: false,
};

TopCategories.propTypes = {
  className: string,
  rootClassName: string,
};

export default TopCategories;
